import {
  ClipboardIcon,
  Pencil01Icon,
  PlusIcon,
} from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AddFeedbackPopoverWithButton,
  AnnotationQueueCrudPane,
} from '@/components/AnnotationQueuePopover/AnnotationQueueCrudPane';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { RunNotesCrudPanel } from '@/components/RunAnnotationCrudPane/RunNotesCrudPanel';
import useToast from '@/components/Toast/useToast';
import { Button } from '@/design-system/components/Button';
import { IconButton } from '@/design-system/components/IconButton';
import {
  AnnotationQueueRubricItem,
  AnnotationQueueSchema,
  FeedbackConfigSchema,
} from '@/types/schema';

import { ExpandableComponent } from './FeedbackRubricShared';

const EXCLUDED_FEEDBACK_KEYS = ['note'];

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

type FeedbackMap<T> = Record<string, T>;

export interface ItemRendererParams<T> {
  rubricItem: AnnotationQueueRubricItem;
  feedbackConfigs?: FeedbackConfigSchema[];
  feedback: T;
  index: number;
  expanded: boolean;
  feedbackFormExpanded: boolean[];
  setFeedbackFormExpanded: React.Dispatch<React.SetStateAction<boolean[]>>;
  autoFocus: boolean;
  isLoading: boolean;
  onAfterSubmit: (index: number, deleted: boolean) => void;
}

type ItemRenderer<T> = (params: ItemRendererParams<T>) => ReactElement | null;

interface BaseQueueRubricFormProps<T> {
  annotationQueue: AnnotationQueueSchema;
  runId: string;
  sessionId: string;

  /* keyed by feedback_key */
  feedbackMap: FeedbackMap<T>;

  /* loading flag (fetching feedback) */
  isLoading: boolean;

  /* whether user can update queue */
  canUpdate: boolean;

  /* feedback configs */
  feedbackConfigs?: {
    data?: FeedbackConfigSchema[];
    mutate: () => void;
  };

  /* called once when feedback is given */
  onFeedbackGiven?: () => void;

  /* renderer for a single item */
  itemRenderer: ItemRenderer<T>;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function BaseQueueRubricForm<T>(props: BaseQueueRubricFormProps<T>) {
  const {
    annotationQueue,
    runId,
    sessionId,
    feedbackMap,
    isLoading,
    canUpdate,
    feedbackConfigs,
    itemRenderer,
    onFeedbackGiven,
  } = props;

  const { createToast } = useToast();

  /* ------------------------------- rubric items -------------------------- */
  const [addedRubricItems, setAddedRubricItems] = useState<
    AnnotationQueueRubricItem[]
  >([]);

  const allRubricItems = useMemo(() => {
    if (
      !annotationQueue.rubric_items &&
      Object.keys(feedbackMap).length === 0 &&
      !addedRubricItems.length
    )
      return undefined;

    const items = [
      ...(annotationQueue.rubric_items ?? []),
      ...addedRubricItems,
    ];
    const seen = new Set<string>(
      items.map((i) => i.feedback_key).filter(Boolean) as string[]
    );

    for (const key of Object.keys(feedbackMap)) {
      if (EXCLUDED_FEEDBACK_KEYS.includes(key)) continue;
      if (!seen.has(key)) {
        items.push({ feedback_key: key });
        seen.add(key);
      }
    }

    return items;
  }, [annotationQueue.rubric_items, addedRubricItems, feedbackMap]);

  /* ---------------------------- expand / collapse ----------------------- */
  const [feedbackFormExpanded, setFeedbackFormExpanded] = useState<boolean[]>([
    true,
  ]);
  const [hasInitExpanded, setHasInitExpanded] = useState(false);

  const setFirstExpandedAfterIndex = useCallback(
    (index: number) => {
      if (!allRubricItems) return;
      setFeedbackFormExpanded((prev) => {
        const next = [...prev];
        if (index >= 0) next[index] = false;
        for (let i = index + 1; i < allRubricItems.length; i++) {
          const item = allRubricItems[i];
          if (!feedbackMap[item.feedback_key ?? '']) {
            next[i] = true;
            break;
          }
        }
        return next;
      });
    },
    [allRubricItems, feedbackMap]
  );

  useEffect(() => {
    if (allRubricItems && !hasInitExpanded) {
      setFeedbackFormExpanded([]);
      setFirstExpandedAfterIndex(-1);
      setHasInitExpanded(true);
    }
  }, [allRubricItems, hasInitExpanded, setFirstExpandedAfterIndex]);

  /* -------------------------- filters configs --------------------------- */
  const filteredFeedbackConfigs = useMemo(() => {
    if (!allRubricItems || !feedbackConfigs?.data) return feedbackConfigs?.data;
    return feedbackConfigs.data.filter(
      (cfg) => !allRubricItems.some((i) => i.feedback_key === cfg.feedback_key)
    );
  }, [allRubricItems, feedbackConfigs?.data]);

  /* ---------------------- instructions dialog --------------------------- */
  const [instrOpen, setInstrOpen] = useState(false);
  const [instrExpanded, setInstrExpanded] = useState(false);
  const [instrTruncated, setInstrTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) setInstrTruncated(el.scrollHeight > el.clientHeight);
  }, [annotationQueue.rubric_instructions, instrExpanded]);

  /* ------------------- feedback given tracking -------------------------- */
  const [hasGiven, setHasGiven] = useState(false);
  const handleFirstFeedback = useCallback(() => {
    if (!hasGiven) {
      setHasGiven(true);
      onFeedbackGiven?.();
    }
  }, [hasGiven, onFeedbackGiven]);

  /* ---------------------------- render ---------------------------------- */
  const instructionsComponent = (
    <div
      className="line-clamp-6 whitespace-pre-wrap break-words text-sm text-disabled"
      ref={textRef}
    >
      {annotationQueue.rubric_instructions}
    </div>
  );

  const instructionsEmpty = (
    <div className="flex flex-col gap-3">
      <div className="text-base font-medium">Instructions</div>
      <div className="flex flex-col items-center gap-2.5 rounded-xl bg-primary p-4">
        <div className="flex justify-around">
          <div className="rounded-full bg-secondary p-2">
            <ClipboardIcon className="h-4 w-4 text-disabled" />
          </div>
        </div>
        <div className="text-sm font-semibold">No instructions yet</div>
        <div className="text-center text-sm font-normal text-disabled">
          {canUpdate
            ? 'Create a clear annotation rubric to ensure consistency and accuracy in annotations.'
            : 'Contact your administrator to create a clear annotation rubric to ensure consistency and accuracy in annotations.'}
        </div>
        {canUpdate && (
          <Button
            size="sm"
            variant="outlined"
            color="secondary"
            onClick={() => setOpen('instructions')}
            leftDecorator={PlusIcon}
          >
            Instructions
          </Button>
        )}
      </div>
    </div>
  );

  const rubricEmpty = (
    <div className="flex flex-col items-center gap-2.5 rounded-xl bg-tertiary p-4">
      <div className="flex justify-around">
        <div className="rounded-full bg-secondary p-2">
          <ClipboardIcon className="h-4 w-4 text-disabled" />
        </div>
      </div>
      <div className="text-sm font-semibold">No feedback yet</div>
      <div className="text-center text-sm font-normal text-disabled">
        You can click "+ Feedback" above to add feedback tags to this specific
        run {canUpdate && 'or set them up in the rubric for future use'}.
      </div>
      {canUpdate && (
        <Button
          size="sm"
          variant="outlined"
          color="secondary"
          onClick={() => setOpen('feedback')}
        >
          Set up rubric
        </Button>
      )}
    </div>
  );

  const [open, setOpen] = useState<'feedback' | 'instructions' | 'edit' | null>(
    null
  );

  return (
    <>
      {/* Main card */}
      <div className="flex flex-col items-stretch gap-4 p-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="text-base font-medium">Annotation Rubric</div>
          {canUpdate && (
            <IconButton
              icon={Pencil01Icon}
              label="Edit"
              variant="plain"
              color="secondary"
              size="sm"
              onClick={() => setOpen('edit')}
            />
          )}
        </div>

        {/* Instructions dialog */}
        <Dialog open={instrOpen} onOpenChange={setInstrOpen}>
          <DialogContent title="Instructions" showClose>
            <div
              className="overflow-y-auto whitespace-pre-wrap break-words text-sm text-disabled"
              style={{ maxHeight: 'min(50vh, 600px)' }}
            >
              {annotationQueue.rubric_instructions}
            </div>
          </DialogContent>
        </Dialog>

        {/* Instructions block */}
        {annotationQueue.rubric_instructions ? (
          <ExpandableComponent
            expandedComponent={
              instrTruncated ? (
                <div className="flex flex-col gap-2">
                  {instructionsComponent}
                  <button
                    type="button"
                    className="self-start text-sm text-quaternary underline"
                    onClick={() => setInstrOpen(true)}
                  >
                    Read more
                  </button>
                </div>
              ) : (
                instructionsComponent
              )
            }
            headerComponent={
              <div className="text-base font-medium">Instructions</div>
            }
            collapsedHeaderComponent={
              <div className="flex items-center gap-3 text-base font-medium">
                Instructions{' '}
                <div className="line-clamp-1 max-h-[20px] break-all text-sm text-disabled">
                  {annotationQueue.rubric_instructions}
                </div>
              </div>
            }
            overrideExpanded={instrExpanded}
            overrideSetExpanded={setInstrExpanded}
          />
        ) : (
          instructionsEmpty
        )}

        {/* Feedback section header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <div className="text-base font-medium">Feedback</div>
              {isLoading && (
                <CircularProgress
                  size="sm"
                  sx={{ opacity: 0.5, transform: 'scale(0.83)' }}
                />
              )}
            </div>
            {filteredFeedbackConfigs && (
              <AddFeedbackPopoverWithButton
                feedbackConfigs={filteredFeedbackConfigs}
                onSelect={(cfg) => {
                  const indexToExpand =
                    (annotationQueue.rubric_items?.length ?? 0) +
                    addedRubricItems.length;
                  setFeedbackFormExpanded((prev) => {
                    const nxt = [...prev];
                    nxt[indexToExpand] = true;
                    return nxt;
                  });
                  setAddedRubricItems((prev) => [
                    ...prev,
                    { feedback_key: cfg.feedback_key },
                  ]);
                }}
                onCreateConfig={() => feedbackConfigs?.mutate()}
                buttonSize="sm"
                buttonTitle="Feedback"
              />
            )}
          </div>

          {/* Feedback rubric */}
          {allRubricItems && allRubricItems.length
            ? allRubricItems.map((item, idx) => {
                const feedback = feedbackMap[item.feedback_key ?? ''];

                return itemRenderer({
                  rubricItem: item,
                  feedbackConfigs: feedbackConfigs?.data,
                  feedback,
                  index: idx,
                  expanded: feedbackFormExpanded[idx],
                  feedbackFormExpanded,
                  setFeedbackFormExpanded,
                  autoFocus: hasGiven,
                  isLoading,
                  onAfterSubmit: (index, del) => {
                    handleFirstFeedback();
                    if (!del) setFirstExpandedAfterIndex(index);
                  },
                });
              })
            : rubricEmpty}
        </div>

        {/* Notes Panel */}
        <RunNotesCrudPanel
          runId={runId}
          sessionId={sessionId}
          title="Reviewer Notes"
        />
      </div>

      {/* Side pane to edit queue */}
      <AnnotationQueueCrudPane
        existingAnnotationQueue={annotationQueue}
        open={!!open}
        focusOn={
          ['instructions', 'feedback'].includes(open ?? '')
            ? (open as 'instructions' | 'feedback')
            : undefined
        }
        closeModal={() => setOpen(null)}
        onSuccess={() =>
          createToast({ title: 'Successfully updated annotation queue' })
        }
      />
    </>
  );
}
